import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import {
  Box,
  Button,
  Modal,
  Typography,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { tyrioUI, ToastHelper, ToastMessageType, ToastType } from '@tyrio/ui-library';
import { DBOrderItems } from '@tyrio/dto';
import { SearchReplaceModal } from './SearchReplaceModal';
import moment from 'moment';
import { useAuth } from '../../../context/AuthContext';
import { LastEditTypographyHeader, LastEditData } from '../styles/AddressCard.style';

interface EditItemsModalProps {
  open: boolean;
  onClose: () => void;
  originalItems: DBOrderItems[];
  currentItems: DBOrderItems[];
  onSave: (updatedItems: DBOrderItems[], editedItemsMetadata: EditedItemsMetadata | null) => void;
  orderId: string;
  editedItemsMetadata?: EditedItemsMetadata | null;
}

export interface EditedItemsMetadata {
  editedBy: string;
  updatedAt: string;
  originalItemsSnapshot: DBOrderItems[];
}

// Simplified product item component without price, discount, and quantity
const SimpleProductItem: React.FC<{
  item: DBOrderItems;
  showReplaceButton?: boolean;
  onReplace?: () => void;
}> = ({ item, showReplaceButton = false, onReplace }) => {
  return (
    <SimpleItemWrapper>
      <ItemContent>
        <ProductTitle>{item.brand} {item.productName}</ProductTitle>
        <ProductDetails>
          <div>EAN: {item.ean}</div>
          <div>IPC: {item.manufacturerCode}</div>
          <div>SKU: {item.sku}</div>
        </ProductDetails>
      </ItemContent>
      {showReplaceButton && (
        <ReplaceButtonContainer>
          <ReplaceButton
            variant="outlined"
            size="small"
            onClick={onReplace}
          >
            REPLACE
          </ReplaceButton>
        </ReplaceButtonContainer>
      )}
    </SimpleItemWrapper>
  );
};

export const EditItemsModal: React.FC<EditItemsModalProps> = ({
  open,
  onClose,
  originalItems,
  currentItems,
  onSave,
  orderId,
  editedItemsMetadata,
}) => {
  const { user } = useAuth();
  const [editedItems, setEditedItems] = useState<DBOrderItems[]>(currentItems);
  const [selectedItemIndex, setSelectedItemIndex] = useState<number | null>(null);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setEditedItems(currentItems);
    setHasChanges(false);
  }, [currentItems]);

  // Check if items have been modified compared to original
  useEffect(() => {
    const itemsChanged = JSON.stringify(editedItems) !== JSON.stringify(originalItems);
    setHasChanges(itemsChanged);
  }, [editedItems, originalItems]);

  const handleReplaceItem = (itemIndex: number) => {
    setSelectedItemIndex(itemIndex);
    setIsSearchModalOpen(true);
  };

  const handleItemReplaced = (newItem: DBOrderItems) => {
    if (selectedItemIndex === null) return;

    const updatedItems = [...editedItems];
    const originalItem = updatedItems[selectedItemIndex];
    
    // Replace the item while keeping quantity and other order-specific data
    updatedItems[selectedItemIndex] = {
      ...originalItem,
      ean: newItem.ean,
      sku: newItem.sku,
      manufacturerCode: newItem.manufacturerCode,
      productName: newItem.productName,
      brand: newItem.brand,
      sellingPrice: newItem.sellingPrice,
    };

    setEditedItems(updatedItems);
    setIsSearchModalOpen(false);
    setSelectedItemIndex(null);
  };

  const handleSubmit = () => {
    if (hasChanges && user) {
      // Create metadata for the edit
      const metadata: EditedItemsMetadata = {
        editedBy: `${user.firstName} ${user.lastName}`,
        updatedAt: new Date().toString(),
        // Use existing originalItemsSnapshot if available, otherwise use currentItems as the original snapshot
        originalItemsSnapshot: editedItemsMetadata?.originalItemsSnapshot || currentItems,
      };
      onSave(editedItems, metadata);
      
      // Show success toast message similar to address editing
      ToastHelper.showToast(
        'Items',
        ToastType.SUCCESS,
        ToastMessageType.UPDATE,
        'Successful items editing! To save your edits, click Save!'
      );
    } else {
      onSave(editedItems, editedItemsMetadata || null);
    }
    onClose();
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '1200px',
            maxWidth: '90vw',
            bgcolor: 'background.paper',
            borderRadius: '16px',
            boxShadow: 24,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            padding: '24px',
          }}
        >
          <CloseWrapper>
            <CloseIcon
              onClick={onClose}
              sx={{ cursor: 'pointer' }}
            />
          </CloseWrapper>
          
          <ContentWrapper>
            {/* Original Items */}
            <ColumnWrapper>
              <TitleWrapper>ORIGINAL ITEMS</TitleWrapper>
              <ItemsContainer>
                <ItemsList>
                  {originalItems.map((item, index) => (
                    <SimpleProductItem key={index} item={item} />
                  ))}
                </ItemsList>
              </ItemsContainer>
            </ColumnWrapper>

            {/* Current Items */}
            <ColumnWrapper>
              <TitleWrapper>CURRENT ITEMS</TitleWrapper>
              <ItemsContainer>
                <ItemsList>
                  {editedItems.map((item, index) => (
                    <SimpleProductItem 
                      key={index} 
                      item={item} 
                      showReplaceButton={true}
                      onReplace={() => handleReplaceItem(index)}
                    />
                  ))}
                </ItemsList>
              </ItemsContainer>
            </ColumnWrapper>
          </ContentWrapper>

          {/* Last Edited Information */}
          {editedItemsMetadata && (
            <LastEditedWrapper>
              <Grid>
                <LastEditTypographyHeader>Last edit: </LastEditTypographyHeader>
                <LastEditData>
                  {editedItemsMetadata.editedBy}
                  {' - '}
                  {moment(editedItemsMetadata.updatedAt).format('DD.MM.YYYY - HH:mm:ss')}
                </LastEditData>
              </Grid>
            </LastEditedWrapper>
          )}

          <ButtonsWrapper>
            <Button
              color="info"
              style={{
                width: '100%',
                height: '60px',
                border: '1px solid rgba(25, 118, 210, 1)',
              }}
              onClick={onClose}
            >
              CANCEL
            </Button>
            <Button
              style={{
                width: '100%',
                height: '60px',
                backgroundColor: 'rgba(25, 118, 210, 1)',
                color: 'white',
              }}
              onClick={handleSubmit}
            >
              {hasChanges ? 'SUBMIT' : 'CLOSE'}
            </Button>
          </ButtonsWrapper>
        </Box>
      </Modal>

      <SearchReplaceModal
        open={isSearchModalOpen}
        onClose={() => {
          setIsSearchModalOpen(false);
          setSelectedItemIndex(null);
        }}
        onItemSelected={handleItemReplaced}
        originalItem={selectedItemIndex !== null ? editedItems[selectedItemIndex] : null}
      />
    </>
  );
};

const CloseWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: end;
  margin-bottom: 16px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  flex: 1;
  min-height: 0;
`;

const ColumnWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const TitleWrapper = styled.div`
  font-family: Barlow;
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 16px;
`;

const ItemsContainer = styled(Box)`
  padding: 16px;
  border: 1px solid #dfe3e8;
  border-radius: 8px;
  height: 500px;
  overflow-y: auto;
  flex: 1;
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SimpleItemWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 0;
  border-bottom: 1px solid ${tyrioUI.colors.black.B40};
  
  &:last-child {
    border-bottom: none;
  }
`;

const ItemContent = styled.div`
  flex: 1;
`;

const ProductTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
  color: ${tyrioUI.colors.black.B100};
`;

const ProductDetails = styled.div`
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #919eab;
  
  @media (max-width: 1250px) {
    flex-direction: column;
    gap: 4px;
  }
`;

const ReplaceButtonContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ReplaceButton = styled(Button)`
  min-width: 80px;
  height: 32px;
  font-size: 12px;
  font-weight: 600;
  border-color: ${tyrioUI.colors.lightBlue};
  color: ${tyrioUI.colors.lightBlue};
  
  &:hover {
    border-color: ${tyrioUI.colors.lightBlue};
    background-color: ${tyrioUI.colors.lightBlue}20;
  }
`;

const ButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  gap: 20px;
  margin-top: 24px;
`;

const LastEditedWrapper = styled.div`
  background-color: #f4f6f8;
  padding: 12px;
  border-radius: 8px;
  margin-top: 24px;
  margin-bottom: 0;
`; 